import React from 'react';
import {
  ResponsiveDialogProvider,
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogTrigger,
} from '@eliadmin/core/src/components/ui/responsive-dialog';
import { Button } from '@eliadmin/core/src/components/ui/button';

interface VideoGuideDialogProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

export const VideoGuideDialog: React.FC<VideoGuideDialogProps> = ({ isOpen, setIsOpen }) => {
  return (
    <ResponsiveDialog open={isOpen} onOpenChange={setIsOpen}>
      <ResponsiveDialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-4xl">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle className="text-xl font-bold">
            Návod ako vytvoriť Videoživotopis
          </ResponsiveDialogTitle>
        </ResponsiveDialogHeader>
        <div className="space-y-6 text-sm">
          <div className="prose prose-sm max-w-none">
            <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4">
              <p className="font-semibold text-blue-800">
                <strong>Buďte stručný.</strong> Dĺžka videoživotopisu by mala trvať ideálne 1–2
                minúty.
              </p>
            </div>

            <div className="mb-6">
              <p className="leading-relaxed text-gray-700">
                Videoživotopis bude Váš moderný spôsob, ako sa uchádzač o prácu, kde sa môžete
                predstaviť zamestnávateľovi osobne a originálne. Obsah Vášho videoživotopisu by mal
                byť stručný, výstižný a prispôsobený konkrétnej pracovnej pozícii. Vo
                videoživotopise by ste mali predstaviť seba, svoje relevantné skúsenosti a úspechy,
                konkrétne zručnosti, motiváciu a ochotu učiť sa a zakončiť výzvou na ďalší kontakt.
                Obsah by mal byť prispôsobený požiadavkám zamestnávateľa a konkrétnej pozícii,
                pričom je dôležité, aby ste pôsobili autenticky a profesionálne.
              </p>
            </div>

            <h3 className="mb-4 text-lg font-semibold text-gray-900">
              Kľúčové body, ktoré by mal obsahovať osobný profesionálny videoživotopis:
            </h3>

            <div className="space-y-6">
              <div>
                <h4 className="mb-2 font-semibold text-blue-600">
                  1. Úvod: Predstavte sa a upútajte pozornosť
                </h4>
                <ul className="ml-4 list-inside list-disc space-y-1 text-gray-700">
                  <li>
                    Uveďte svoje meno, pozíciu, o ktorú sa uchádzate, a dôvod, prečo ste si ju
                    vybrali.
                  </li>
                  <li>
                    Krátko vysvetlite, čo vás na danej pracovnej pozícii zaujíma a čo vás motivuje
                    sa v nej zlepšovať.
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="mb-2 font-semibold text-blue-600">
                  2. Profesijné zhrnutie a silné stránky
                </h4>
                <ul className="ml-4 list-inside list-disc space-y-1 text-gray-700">
                  <li>
                    Spomeňte svoje najdôležitejšie pracovné skúsenosti a úspechy, ktoré sú
                    relevantné pre danú pozíciu.
                  </li>
                  <li>
                    Vyzdvihnite silné vlastnosti a zručnosti, ktoré vás odlišujú (napr. dôslednosť,
                    spoľahlivosť, samostatnosť, vášeň pre danú profesiu).
                  </li>
                  <li>Podporte tieto tvrdenia konkrétnymi príkladmi z praxe.</li>
                </ul>
              </div>

              <div>
                <h4 className="mb-2 font-semibold text-blue-600">
                  3. Pracovné skúsenosti a vaše úspechy
                </h4>
                <ul className="ml-4 list-inside list-disc space-y-1 text-gray-700">
                  <li>
                    Uveďte najnovšie a najrelevantnejšie pracovné skúsenosti, prípadne aj stáže
                    alebo brigády, ak ste absolvent.
                  </li>
                  <li>
                    Pri každej pracovnej skúsenosti stručne popíšte svoju náplň práce a zvýraznite
                    konkrétne úspechy alebo projekty, ktoré ste realizovali.
                  </li>
                  <li>
                    Ak je to možné, uveďte výsledky číselne (napr. zvýšenie predaja, počet
                    dokončených projektov).
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="mb-2 font-semibold text-blue-600">4. Zručnosti a znalosti</h4>
                <ul className="ml-4 list-inside list-disc space-y-1 text-gray-700">
                  <li>
                    Zdôraznite odborné, technické aj mäkké zručnosti, ktoré sú dôležité pre pozíciu
                    (napr. tímová práca, riešenie problémov, zvládanie konfliktov, časový
                    manažment).
                  </li>
                  <li>
                    Uveďte úroveň ovládania dôležitých programov alebo jazykov (začiatočník,
                    pokročilý, expert).
                  </li>
                  <li>
                    Spomeňte relevantné kurzy, certifikáty či školenia, ktoré ste absolvovali.
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="mb-2 font-semibold text-blue-600">5. Motivácia a ochota učiť sa</h4>
                <ul className="ml-4 list-inside list-disc space-y-1 text-gray-700">
                  <li>
                    Vyjadrite záujem o ďalšie vzdelávanie a rozvoj v oblasti, ktorá je pre
                    zamestnávateľa dôležitá.
                  </li>
                  <li>Spomeňte, aké nové zručnosti by ste sa chceli naučiť a prečo.</li>
                </ul>
              </div>

              <div>
                <h4 className="mb-2 font-semibold text-blue-600">
                  6. Záver: Výzva na akciu a poďakovanie
                </h4>
                <ul className="ml-4 list-inside list-disc space-y-1 text-gray-700">
                  <li>
                    Poďakujte za pozornosť a vyjadrite záujem o osobné stretnutie alebo ďalšiu
                    komunikáciu.
                  </li>
                  <li>Prípadne stručne zhrňte, prečo ste vhodným kandidátom na danú pozíciu.</li>
                </ul>
              </div>
            </div>

            <div className="mt-8">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">
                Praktické tipy pre videoživotopis
              </h3>
              <ul className="ml-4 list-inside list-disc space-y-2 text-gray-700">
                <li>Buďte stručný – ideálne 1–2 minúty.</li>
                <li>Hovorte jasne, zrozumiteľne a s primeraným nadšením.</li>
                <li>Prispôsobte obsah konkrétnej pracovnej pozícii.</li>
                <li>Dbajte na profesionálny vzhľad a prostredie pri natáčaní.</li>
                <li>Ak máte časový priestor vo videoživotopise priložte ukážky vašej práce.</li>
                <li>
                  <strong>Odporúčanie:</strong> Aby ste boli vo videoživotopise stručný,
                  zrozumiteľný a pripravený, pred natočením videoživotopisu si najprv napíšte text,
                  ktorý chcete nahrať do videoživotopisu.
                </li>
                <li>Buďte kreatívny.</li>
              </ul>
            </div>

            <div className="mt-8 rounded-lg border border-green-200 bg-green-50 p-4">
              <h4 className="mb-2 font-semibold text-green-800">Zhrnutie:</h4>
              <p className="text-green-800">
                Vo videoživotopise by mal uchádzač predstaviť seba, svoje relevantné skúsenosti a
                úspechy, konkrétne zručnosti, motiváciu a ochotu učiť sa, a zakončiť výzvou na ďalší
                kontakt. Obsah by mal byť prispôsobený požiadavkám zamestnávateľa a konkrétnej
                pozícii, pričom dôležité je pôsobiť autenticky a profesionálne.
              </p>
            </div>
          </div>
        </div>
        <div className="mt-6 flex justify-end">
          <Button onClick={() => setIsOpen(false)}>Zavrieť</Button>
        </div>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
};
